function parseLocalizedPrice(priceString) {
  let cleaned = priceString
    .replace(/[^\d.,]/g, '') // Uklanja valute i razmake

  // Ako ima samo jedan zarez i NEMA decimala iza
  if (/^\d{1,3}([.,]\d{3})+$/.test(cleaned)) {
    // Primer: "1,458" → 1458
    cleaned = cleaned.replace(/[.,]/g, '');
    return parseFloat(cleaned);
  }

  // Ako ima i decimalni deo
  cleaned = cleaned
    .replace(/\.(?=\d{3})/g, '') // Ukloni hiljadarke sa tačkom
    .replace(',', '.');          // Zameni decimalni zarez tačkom

  return parseFloat(cleaned);
}


class SubscriptionFlow extends HTMLElement {
  constructor(element) {
    super();
    element ? (this.element = element) : (this.element = this);
    this.delegateElement = new Delegate(this.element);
    this.bodyDelegate = new Delegate(document.body);
  }

  getSubsBlock() {
    return (
      this.element.closest(".pdp-subscription_block") ||
      document.body.querySelector(".pdp-subscription_block")
    );
  }

  // Only ever write to the form of the same product-component. Never rename it:
  // selling_plan / gift inputs are linked to it through their form="" attribute.
  getProductForm() {
    const scope = this.productComponent || document;
    const formId = this.element.dataset.formId;
    const byId = formId
      ? Array.from(scope.querySelectorAll("form")).find((form) => form.id === formId)
      : null;
    return byId || scope.querySelector("form.mainPDP--formBtns");
  }

  isOwnEvent(target) {
    if (!this.productComponent || !(target instanceof Element)) return true;
    return target.closest("product-component") === this.productComponent;
  }

  isPageProductOption(option) {
    return option?.dataset.id === this.pageProductId;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.productComponent = this.element.closest("product-component");
    this.form = this.getProductForm();
    this.pageProductId = this.element.dataset.productid || "";
    this.pageVariantId = this.form?.querySelector('input[name="id"]')?.value || "";
    this.hideOneTime = this.element.dataset.hideOneTime === "true";

    this.calculateMinPrice();

    this.onVariantSubs = this.refreshSubsBlock.bind(this);
    document.body.addEventListener("variant:subs", this.onVariantSubs);

    this.delegateElement.on(
      "click",
      '[data-action="subscription-change"]',
      this.toggleSubscription.bind(this)
    );

    this.bodyDelegate.on(
      "click",
      '[data-action="reveal-one-time"]',
      this.revealOneTime.bind(this)
    );

    this.delegateElement.on(
      "click",
      "[data-gift-toggle]",
      this.onGiftToggle.bind(this)
    );

    this.delegateElement.on(
      "click",
      "[data-gift-option]",
      this.onGiftOptionClick.bind(this)
    );

    this.delegateElement.on(
      "click",
      "[data-after-toggle]",
      this.onAfterToggle.bind(this)
    );

    this.selectGift(
      this.element.querySelector("[data-gift-option].is-selected") ||
        this.element.querySelector("[data-gift-option]")
    );

    this.bindGiftScrollerWheel();
    this.bindGiftStickyGuard();
    this.setPreferOneTimeVisible(false);
    this.preselect();
    this.syncCollapsedPanels();
  }

  disconnectedCallback() {
    this.delegateElement?.off();
    this.bodyDelegate?.off();
    if (this.onVariantSubs) {
      document.body.removeEventListener("variant:subs", this.onVariantSubs);
      this.onVariantSubs = null;
    }
    this.unbindGiftScrollerWheel();
    this.unbindGiftStickyGuard();
    document.documentElement.classList.remove("gift-picker-open");
    this.initialized = false;
  }

  // Vertical page scroll must work when a gesture starts on a gift image.
  // CSS touch-action: pan-y lets the browser handle vertical; we only take over
  // once the gesture locks to horizontal (mouse drag or touch swipe).
  bindGiftScrollerWheel() {
    this.unbindGiftScrollerWheel();
    this.giftWheelCleanups = [];

    this.element.querySelectorAll(".Product--SubscriptionGiftScroller").forEach((scroller) => {
      const onWheel = (e) => {
        if (e.deltaX !== 0 && Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;

        if (e.shiftKey) {
          scroller.scrollLeft += e.deltaY;
          e.preventDefault();
          return;
        }

        if (e.deltaY === 0) return;

        e.preventDefault();
        window.scrollBy(0, e.deltaY);
      };

      let drag = null;

      const onPointerDown = (e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;

        drag = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          startScroll: scroller.scrollLeft,
          moved: false,
          captured: false,
          axis: null,
        };
      };

      const onPointerMove = (e) => {
        if (!drag || drag.pointerId !== e.pointerId) return;

        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;

        if (!drag.axis) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          // Vertical intent → drop drag so the page can scroll.
          if (Math.abs(dy) > Math.abs(dx)) {
            drag = null;
            return;
          }
          drag.axis = "x";
        }

        if (drag.axis !== "x") return;

        if (!drag.captured) {
          drag.captured = true;
          scroller.classList.add("is-dragging");
          try {
            scroller.setPointerCapture(e.pointerId);
          } catch (_) {
            /* capture can fail if the pointer was already released */
          }
        }

        drag.moved = true;
        scroller.scrollLeft = drag.startScroll - dx;
        e.preventDefault();
      };

      const endDrag = (e) => {
        if (!drag || drag.pointerId !== e.pointerId) return;

        const wasDrag = drag.moved;
        drag = null;
        scroller.classList.remove("is-dragging");

        // Suppress the click that would select a gift after a horizontal drag.
        if (wasDrag) {
          const suppressClick = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            scroller.removeEventListener("click", suppressClick, true);
          };
          scroller.addEventListener("click", suppressClick, true);
        }
      };

      scroller.addEventListener("wheel", onWheel, { passive: false });
      scroller.addEventListener("pointerdown", onPointerDown);
      scroller.addEventListener("pointermove", onPointerMove);
      scroller.addEventListener("pointerup", endDrag);
      scroller.addEventListener("pointercancel", endDrag);

      this.giftWheelCleanups.push(() => {
        scroller.removeEventListener("wheel", onWheel);
        scroller.removeEventListener("pointerdown", onPointerDown);
        scroller.removeEventListener("pointermove", onPointerMove);
        scroller.removeEventListener("pointerup", endDrag);
        scroller.removeEventListener("pointercancel", endDrag);
        scroller.classList.remove("is-dragging");
      });
    });
  }

  unbindGiftScrollerWheel() {
    (this.giftWheelCleanups || []).forEach((cleanup) => cleanup());
    this.giftWheelCleanups = [];
  }

  // Sticky ATC from the main PDP covers the gift Change control — hide it while
  // the gift block is on screen so the picker can be opened and used.
  bindGiftStickyGuard() {
    this.unbindGiftStickyGuard();
    const gift = this.element.querySelector("[data-gift]");
    if (!gift || typeof IntersectionObserver === "undefined") return;

    this.giftStickyObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        document.documentElement.classList.toggle("gift-section-visible", visible);
      },
      { rootMargin: "0px 0px -72px 0px", threshold: 0 }
    );
    this.giftStickyObserver.observe(gift);
  }

  unbindGiftStickyGuard() {
    this.giftStickyObserver?.disconnect();
    this.giftStickyObserver = null;
    document.documentElement.classList.remove("gift-section-visible");
  }

  // Collapsed panels are only visually hidden (opacity / 0fr), so keep them out of the tab order.
  syncCollapsedPanels() {
    this.element.querySelectorAll(".Product--SubscriptionOption").forEach((option) => {
      const details = option.querySelector(".Product--SubscriptionDetails");
      if (details) details.inert = option.getAttribute("aria-selected") !== "true";
    });

    this.element.querySelectorAll("[data-gift]").forEach((giftRoot) => {
      const picker = giftRoot.querySelector("[data-gift-picker]");
      if (picker) picker.inert = !giftRoot.classList.contains("is-picking");
    });

    this.element.querySelectorAll(".Product--SubscriptionAfter").forEach((afterRoot) => {
      const details = afterRoot.querySelector(".Product--SubscriptionAfterDetails");
      if (details) details.inert = !afterRoot.classList.contains("is-open");
    });
  }

  onAfterToggle(e) {
    e.preventDefault();
    e.stopPropagation();
    const afterRoot = e.target.closest(".Product--SubscriptionAfter");
    if (!afterRoot) return;

    this.setAfterOpen(afterRoot, !afterRoot.classList.contains("is-open"));
  }

  // Gift picker and "What happens after" never stay open together.
  setAfterOpen(afterRoot, open) {
    if (!afterRoot) return;

    if (open) {
      this.element.querySelectorAll("[data-gift].is-picking").forEach((giftRoot) => {
        this.setGiftPickerOpen(giftRoot, false);
      });
    }

    afterRoot.classList.toggle("is-open", open);

    const toggle = afterRoot.querySelector("[data-after-toggle]");
    if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
    this.syncCollapsedPanels();
  }

  onGiftToggle(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggleGiftPicker(e.target.closest("[data-gift]"));
  }

  onGiftOptionClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.selectGift(e.target.closest("[data-gift-option]"));
  }

  setGiftPickerOpen(giftRoot, picking) {
    if (!giftRoot) return;

    const giftPicker = giftRoot.querySelector("[data-gift-picker]");
    const giftToggle = giftRoot.querySelector("[data-gift-toggle]");
    const giftToggleLabel = giftRoot.querySelector("[data-gift-toggle-label]");

    if (picking) {
      this.element.querySelectorAll(".Product--SubscriptionAfter.is-open").forEach((afterRoot) => {
        this.setAfterOpen(afterRoot, false);
      });
    }

    giftRoot.classList.toggle("is-picking", picking);
    // Sticky ATC sits over the gift row on short viewports — tuck it away while picking.
    document.documentElement.classList.toggle("gift-picker-open", picking);
    if (giftPicker) giftPicker.setAttribute("aria-hidden", picking ? "false" : "true");
    if (giftToggle) giftToggle.setAttribute("aria-expanded", picking ? "true" : "false");
    if (giftToggleLabel) {
      const nextLabel = picking
        ? this.element.dataset.giftClose
        : this.element.dataset.giftChange;
      if (nextLabel) giftToggleLabel.textContent = nextLabel;
    }
    this.syncCollapsedPanels();

    if (picking) {
      giftRoot.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  toggleGiftPicker(giftRoot) {
    if (!giftRoot) return;

    this.setGiftPickerOpen(giftRoot, !giftRoot.classList.contains("is-picking"));
  }

  selectGift(option) {
    if (!option) return;

    const giftOptions = this.element.querySelectorAll("[data-gift-option]");
    const giftIdInput = this.element.querySelector("[data-welcome-gift-id-input]");
    const giftId = option.dataset.giftId || "";
    const title = option.dataset.giftTitle || "";
    const meta = option.dataset.giftMeta || "";
    const image = option.dataset.giftImage || "";
    const price = option.dataset.giftPrice || "";

    // Same gift is rendered once per plan, so match by variant id (titles can repeat).
    giftOptions.forEach((el) => {
      const selected = el.dataset.giftId === giftId;
      el.classList.toggle("is-selected", selected);
      el.setAttribute("aria-pressed", selected ? "true" : "false");
    });

    this.element.querySelectorAll("[data-gift-name]").forEach((el) => {
      el.textContent = title;
    });

    this.element.querySelectorAll("[data-gift-summary-meta]").forEach((el) => {
      el.textContent = meta;
    });

    // "worth [gift_price]" in the benefits list
    if (price) {
      this.element.querySelectorAll("[data-gift-worth]").forEach((el) => {
        el.textContent = price;
      });
    }

    this.element.querySelectorAll("[data-gift-summary-image]").forEach((el) => {
      if (image) {
        el.src = image;
        el.removeAttribute("srcset");
        el.sizes = "48px";
        el.alt = title;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    this.element.querySelectorAll("[data-gift-summary-placeholder]").forEach((el) => {
      el.hidden = Boolean(image);
    });

    // The picker stays open; only "Close" returns to the summary.
    if (giftIdInput) giftIdInput.value = giftId;
  }

  syncGiftInput(isOneTime) {
    this.element.querySelectorAll("[data-welcome-gift-id-input]").forEach((input) => {
      input.disabled = Boolean(isOneTime);
    });
  }

  getPreferOneTimeButtons() {
    const root = this.productComponent || document;
    return root.querySelectorAll('[data-action="reveal-one-time"]');
  }

  setPreferOneTimeVisible(visible) {
    const show = Boolean(visible) && this.hideOneTime;
    this.getPreferOneTimeButtons().forEach((btn) => {
      btn.hidden = !show;
      btn.disabled = !show;
      btn.setAttribute("aria-disabled", show ? "false" : "true");
      btn.tabIndex = show ? 0 : -1;
    });
  }

  revealOneTime(e) {
    const trigger = e.target.closest('[data-action="reveal-one-time"]');
    if (!this.isOwnEvent(trigger)) return;

    e.preventDefault();
    if (trigger?.disabled || trigger?.getAttribute("aria-disabled") === "true") return;

    this.selectOneTime();
  }

  applyOptionSelection(option) {
    if (!option) return;

    const oneTimeButton = this.element.querySelector(".Product--SubscriptionOneTimeButton");
    const subscriptionButton = this.element.querySelector(
      ".Product--SubscriptionButton[aria-selected='true']"
    );

    this.updateSubscriptionNames(option);

    if (option.dataset.value === "one_time") {
      this.switchToOneTime(oneTimeButton, subscriptionButton);
      this.syncGiftInput(true);
    } else {
      this.switchToSubscription(option, oneTimeButton, subscriptionButton);
      this.syncGiftInput(false);
    }

    this.updateSellingPlanInput(option);
    this.syncCollapsedPanels();

    const priceEl = option.querySelector("[data-price]");
    const price = priceEl?.dataset?.price || option.dataset.price;
    this.element.dispatchEvent(
      new CustomEvent("price:change", {
        bubbles: true,
        detail: {
          eventName: "subs",
          price,
        },
      })
    );
  }

  selectOneTime() {
    const oneTimeButton = this.element.querySelector(".Product--SubscriptionOneTimeButton");
    if (!oneTimeButton) return;
    this.applyOptionSelection(oneTimeButton);
    requestAnimationFrame(() => {
      this.syncCollapsedPanels();
    });
  }

  // Default = Most Popular if present, else a page-product plan, else any plan.
  // One-time is only a fallback when no subscription plans are available.
  preselect() {
    const visiblePlans = Array.from(
      this.element.querySelectorAll(
        ".Product--SubscriptionButton:not([aria-hidden='true'])"
      )
    );

    const mostPopular = visiblePlans.find((plan) =>
      plan.classList.contains("is-most-popular")
    );
    const pagePlans = visiblePlans.filter((plan) => this.isPageProductOption(plan));
    const target = mostPopular || pagePlans[0] || visiblePlans[0];

    if (target) {
      this.applyOptionSelection(target);
      return;
    }

    this.selectOneTime();
  }

  toggleAddButton(){
    this.price = this.element.getAttribute('data-min-price');
    this.priceEl = document.querySelectorAll('.mainProductPrice');

    this.priceEl.forEach((element) =>{
      element.innerHTML = Shopify.formatMoney(
        this.price,
        window.theme.moneyFormat
      );
    })
  }

  // Keep the current choice on variant change; fall back to one-time only when
  // the new variant has no selling plans.
  refreshSubsBlock(event) {
    if (!this.isOwnEvent(event.target)) return;

    if (event.detail?.variant_id) this.pageVariantId = String(event.detail.variant_id);

    const hasPlans = Number(event.detail?.plans_length) > 0;
    this.getSubsBlock()?.setAttribute("aria-expanded", hasPlans ? "true" : "false");

    if (!hasPlans) {
      this.selectOneTime();
      return;
    }

    const selected = this.element.querySelector(
      '.Product--SubscriptionOption[aria-selected="true"]'
    );
    if (selected) {
      selected.click();
    } else {
      this.preselect();
    }
  }

  calculateMinPrice() {
    const plans = this.element.querySelectorAll('.Product--SubscriptionOption.Product--SubscriptionButton');
    let pricesFromPlans = [];
  
    plans.forEach(plan => {
      const raw = (plan.dataset.priceint || "").trim();
      if (!raw) return;
      const price = parseLocalizedPrice(raw);
      pricesFromPlans.push(price);
    });
  
    if (!pricesFromPlans.length) return;

    const minPrice = Math.min(...pricesFromPlans);
    this.element.setAttribute('data-min-price', minPrice.toFixed(2));
  }

  toggleSubscription(e) {
    if (e.target.closest("[data-gift-toggle], [data-gift-option], [data-after-toggle], .Product--SubscriptionGiftChange, .Product--SubscriptionAfter")) {
      return;
    }

    e.preventDefault();

    const parentElement = e.target.closest(".Product--SubscriptionOption");
    if (!parentElement) return;

    this.applyOptionSelection(parentElement);
  }
  
  updateSubscriptionNames(parentElement) {
    const root = this.productComponent || document;
    const updateSubs = root.querySelectorAll('[data-action="subs-update"]');
    const nameEl = parentElement.querySelector(".Product--SubscriptionName");
    if (!nameEl) return;
    updateSubs.forEach((box) => {
      box.innerHTML = nameEl.innerHTML;
    });
  }
  
  switchToOneTime(oneTimeButton, subscriptionButton) {
    if (subscriptionButton) {
      this.toggleCheckboxDisplay(subscriptionButton, true);
      subscriptionButton.setAttribute("aria-selected", "false");
    }

    this.element.querySelectorAll(".Product--SubscriptionButton[aria-selected='true']").forEach((btn) => {
      this.toggleCheckboxDisplay(btn, true);
      btn.setAttribute("aria-selected", "false");
    });
  
    if (oneTimeButton) {
      oneTimeButton.setAttribute("aria-hidden", "false");
      this.toggleCheckboxDisplay(oneTimeButton, false);
      oneTimeButton.setAttribute("aria-selected", "true");
    }

    this.setPreferOneTimeVisible(false);
  }
  
  switchToSubscription(parentElement, oneTimeButton, subscriptionButton) {
    this.element.querySelectorAll(".Product--SubscriptionButton").forEach((btn) => {
      this.toggleCheckboxDisplay(btn, true);
      btn.setAttribute("aria-selected", "false");
    });

    if (oneTimeButton) {
      this.toggleCheckboxDisplay(oneTimeButton, true);
      oneTimeButton.setAttribute("aria-selected", "false");
      oneTimeButton.setAttribute("aria-hidden", this.hideOneTime ? "true" : "false");
    }
  
    this.toggleCheckboxDisplay(parentElement, false);
    parentElement.setAttribute("aria-selected", "true");
    this.setPreferOneTimeVisible(true);
  }
  
  // Visibility is CSS-driven via [aria-selected]; clear any leftover inline styles.
  toggleCheckboxDisplay(element, show) {
    if (!element) return;
    const unchecked = element.querySelector(
      ".Product--SubscriptionFlex > .flex:first-child > .Product--SubscriptionCheckbox"
    );
    const checked = element.querySelector(
      ".Product--SubscriptionFlex > .flex:first-child > .Product--SubscriptionCheckboxChecked"
    );
    if (unchecked) unchecked.style.removeProperty("display");
    if (checked) checked.style.removeProperty("display");
  }
  
  // Plans of the page product (and one-time) use the currently selected page variant;
  // a subscriptions_block plan swaps in its own product's variant.
  updateSellingPlanInput(parentElement) {
    const isOneTime = parentElement.dataset.value === "one_time";

    const sellingPlanInput = this.element.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) {
      sellingPlanInput.value = isOneTime ? "" : parentElement.dataset.plan || "";
    }

    const idInput = this.form?.querySelector('input[name="id"]');
    if (!idInput) return;

    const variantId =
      isOneTime || this.isPageProductOption(parentElement)
        ? this.pageVariantId
        : parentElement.dataset.value;
    if (!variantId) return;

    idInput.value = variantId;
    idInput.setAttribute("value", variantId);
  }
}
customElements.define("subscription-flow", SubscriptionFlow);
